import React, {useEffect, useState} from "react";
import {Paper} from "@mui/material";
import {Col} from "reactstrap";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import Button from "../../../../../../../components/base/button/button";
import WarrantyAndReturnExtraFeesForm, {ExtraFeesValueTypes} from "../../../../../../../components/app-specific/part-support/part-support-setting-cards/extra-fee-form";
import TryAgain from "../../../../../../../components/app-specific/try-again";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import WarrantyAndReturnExtraFeesCard from "../../../../../../../components/app-specific/part-support/part-support-setting-cards/extra-fee-card";
import useQuery from "../../../../../../../hooks/use-query";

const WarrantyAndReturnProcessSettingsExtraFees = ({fees: feesProps, titles, openTitleManagement, loading, section}) => {
    const [query] = useQuery()
    const [creating, setCreating] = useState(false)
    const [fees, setFees] = useState([])
    const isMounted = useIsMounted()

    /**
     * Listens for the changes in feesProps and with each change:
     * replaces the state list with the given props.
     */
    useEffect(() => {
        setFees(feesProps?.map(e => ({...e, editing: false})) ?? [])
    }, [feesProps])

    /**
     * Create a new Extra fee with the given values.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createExtraFee = async (values, form) => {
        setCreating(true)
        const forApi = {
            isPercent: values.type?.id === ExtraFeesValueTypes.percent.id,
            value: values.fee,
            minDays: parseInt(values.minDays),
            maxDays: parseInt(values.maxDays),
            titleId: values.title?.id,
            partNo: null,
            partSupportReqTypeId: parseInt(`${query?.typeId}`),
            categoryId: parseInt(`${query?.categoryId}`)
        }
        const response = await Api.createPartSupportRequestExtraFees(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            const newFee = {
                ...response.data,
                editing: false,
            }
            setFees(prevState => [
                newFee,
                ...prevState
            ])
            setTimeout(() => form.restart({}), 0)

        }
        setCreating(false)
        return false
    }

    /**
     * Updates an Extra fee with the given values.
     * @param {any} fee the fee to be updated
     * @return {Promise<boolean>}
     */
    const updateExtraFee = async (fee) => {
        const forApi = {
            ...fee,
            PartSupportReqTypeId: parseInt(query.typeId),
            CategoryId: parseInt(query.categoryId),
            TitleId: fee.titleInfo?.id,
            partNo: null,
        }
        const response = await Api.editPartSupportRequestExtraFees(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setFees(prevState => prevState.map(e => e.id === fee.id ? {...e, ...response.data} : e))
        }
        return response?.resultFlag ?? false
    }

    /**
     * Removes an extra fee from the server and then removes it from the local list.
     * @param {any} fee the extra fees to be removed
     */
    const removeExtraFee = (fee) => {
        setFees(prevState => prevState.map(e => e.id === fee.id ? {...e, editing: true} : e))
        Api.removePartSupportRequestExtraFees(fee.id).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setFees(prevState => prevState.filter(e => e.id !== fee.id))
            } else {
                setFees(prevState => prevState.map(e => e.id === fee.id ? {...e, editing: false} : e))
            }
        })
    }


    return (
        <>
            <Paper
                id={section.id}
                className='extra-fees-paper paper w-100 px-3 py-2 flex-wrap justify-content-between align-items-start'>
                <Col xs={12}>
                    <p className={'text-lg font-weight-bold mt-4 mb-4'}>
                        {section.name}
                    </p>
                </Col>
                <div className={'form-section creation'}>
                    <Col xs={12}>
                        <p className={'text-md font-weight-bold mt-2 mb-4'}>
                            Add a new Period
                        </p>
                    </Col>
                    <WarrantyAndReturnExtraFeesForm
                        onSubmit={createExtraFee}
                        titles={titles}
                        openTitleManagement={openTitleManagement}
                    >
                        {() => (
                            <Col xs={12} className={' d-flex justify-content-end'}>
                                <Button
                                    className='px-5'
                                    type={'submit'}
                                    color={'primary'}
                                    variant={'contained'}
                                    disabled={creating}
                                >
                                    {
                                        creating
                                            ? "Submitting..."
                                            : "Submit"
                                    }
                                </Button>
                            </Col>
                        )}
                    </WarrantyAndReturnExtraFeesForm>
                </div>
                <Col xs={12} className={'mt-3 d-flex flex-wrap'}>
                    {
                        loading
                            ? Array(5).fill(null).map((e, i) => (
                                <div key={i} className={'loading normal w-100 mb-3'}>
                                    <div/>
                                </div>
                            ))
                            : fees?.length < 1
                                ? <TryAgain
                                    text={"There is currently no extra fees submitted. you can add one" +
                                        " using the form above."}
                                />
                                : <TransitionGroup className={'d-flex flex-wrap w-100'}>
                                    {
                                        fees?.map(fee => (
                                            <CSSTransition
                                                key={fee.id}
                                                classNames='item'
                                                timeout={500}
                                            >
                                                <WarrantyAndReturnExtraFeesCard
                                                    fee={fee}
                                                    titles={titles}
                                                    openTitleManagement={openTitleManagement}
                                                    update={updateExtraFee}
                                                    remove={removeExtraFee}
                                                />
                                            </CSSTransition>
                                        ))
                                    }
                                </TransitionGroup>
                    }
                </Col>
            </Paper>
        </>
    );
}

export default WarrantyAndReturnProcessSettingsExtraFees;
